<template>
  <div>
    <div class="row mt-4">
      <div class="col-md-8">
        <input class="input form-control"
               @keypress.enter="find()"
               v-model="query.generic"
               placeholder="Pesquisar por Nome ou CNPJ">
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <button class="col-md-1 ml-2 btn-sm btn-outline"
              :class="{ 'btn-outline-active': showFilter }"
              @click="showFilter = !showFilter">
        Filtros
        <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': showFilter }"/>
      </button>

      <button v-if="changeQuery" @click="find()" class="btn btn-link">
        Aplicar filtros ({{ numberOfBadges }})
      </button>
    </div>

    <div class="row">
      <div class="col-md-12">
        <BadgeFilters :query="query" @clearBadge="find()" @clearAllBadges="find()"
                      :amount-changed="updateNumberOfBadges"
                      :filters="[filterId, filterCityCode, filterIe, filterIm, filterCompanyId, filterType, filterEntityStatus, filterUf, filterCertificate, filterEmail, filterUpdatedBy, filterAccount, filterStartDate, filterEndDate, filterPeriodType]"/>
      </div>
    </div>

    <template v-if="showFilter">
      <!-- Primeira linha: 5 colunas iguais -->
      <div class="row mt-4">
        <div class="col">
          <label for="company-id">Contas</label>
          <Select2 :options="filterAccount" v-model="query.accountId" id="company-id" title-load="Carregando contas..."/>
        </div>

        <div class="col">
          <label for="company-city-code">Código da Cidade</label>
          <input v-model="query.cityCode" type="text" id="company-city-code" class="form-control">
        </div>

        <div class="col">
          <label for="company-ie">Inscrição Estadual</label>
          <input v-model="query.ie" type="text" id="company-ie" class="form-control">
        </div>

        <div class="col">
          <label for="company-im">Inscrição Municipal</label>
          <input v-model="query.im" type="text" id="company-im" class="form-control">
        </div>

        <div class="col">
          <label for="company-parent-id">ID da Empresa Matriz</label>
          <input v-model="query.companyId" type="text" id="company-parent-id" class="form-control">
        </div>
      </div>

      <!-- Segunda linha: 5 colunas iguais -->
      <div class="row mt-2">
        <div class="col">
          <label for="company-type">Tipo</label>
          <select id="company-type" v-model="query.type" class="form-control">
            <option value="">Todos</option>
            <option value="parent">Matriz</option>
            <option value="subsidiary">Filial</option>
          </select>
        </div>

        <div class="col">
          <label for="company-entity-status">Status</label>
          <select id="company-entity-status" v-model="query.entityStatusId" class="form-control">
            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>

        <div class="col">
          <label for="company-uf">UF</label>
          <select id="company-uf" v-model="query.uf" class="form-control">
            <option value="">Todos</option>
            <option value="AC">AC</option>
            <option value="AL">AL</option>
            <option value="AP">AP</option>
            <option value="AM">AM</option>
            <option value="BA">BA</option>
            <option value="CE">CE</option>
            <option value="DF">DF</option>
            <option value="ES">ES</option>
            <option value="GO">GO</option>
            <option value="MA">MA</option>
            <option value="MT">MT</option>
            <option value="MS">MS</option>
            <option value="MG">MG</option>
            <option value="PA">PA</option>
            <option value="PB">PB</option>
            <option value="PR">PR</option>
            <option value="PE">PE</option>
            <option value="PI">PI</option>
            <option value="RJ">RJ</option>
            <option value="RN">RN</option>
            <option value="RS">RS</option>
            <option value="RO">RO</option>
            <option value="RR">RR</option>
            <option value="SC">SC</option>
            <option value="SP">SP</option>
            <option value="SE">SE</option>
            <option value="TO">TO</option>
          </select>
        </div>

        <div class="col">
          <label for="company-certificate">Certificado</label>
          <select id="company-certificate" v-model="query.has_certificate" class="form-control">
            <option value="">Todos</option>
            <option value="true">Com certificado</option>
            <option value="false">Sem certificado</option>
          </select>
        </div>

        <div class="col">
          <label for="company-user">Criado por</label>
          <Select2 :options="filterEmail" v-model="query.user_email" id="company-user" title-load="Carregando emails..."/>
        </div>

        <div class="col">
          <label for="company-updated-by">Atualizado por</label>
          <Select2 :options="filterUpdatedBy" v-model="query.updated_by" id="company-updated-by" title-load="Carregando emails..."/>
        </div>
      </div>

      <!-- Terceira linha: 4 colunas iguais -->
      <div class="row mt-2">
        <div class="col-md-3">
          <label for="period-type">Tipo de período</label>
          <select id="period-type" v-model="query.periodType" class="form-control">
            <option value="created_at">Data de criação</option>
            <option value="updated_at">Data de atualização</option>
            <option value="certificate_expiring_at">Expiração do certificado</option>
          </select>
        </div>

        <div class="col-md-3">
          <label for="start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control">
        </div>

        <div class="col-md-3">
          <label for="end-date">Data final</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control">
        </div>

        <div class="col-md-3">
          <label for="company-order">Ordenar por</label>
          <select id="company-order" class="form-control" @change="updateOrder($event)">
            <option value="1">Nome ascendente</option>
            <option value="2">Nome descendente</option>
            <option value="3">CNPJ ascendente</option>
            <option value="4">CNPJ descendente</option>
            <option value="5">Criação ascendente</option>
            <option value="6">Criação descendente</option>
            <option value="7">Atualização ascendente</option>
            <option value="8">Atualização descendente</option>
          </select>
        </div>
      </div>
    </template>
    <hr>

    <!-- Buttons -->
    <div class="row mt-2">
      <div class="col-md-12">
        <button
          class="btn btn-primary mt-1 mr-1"
          @click="createReport"
          :disabled="disabledReport"
        >
          Baixar relatório
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="row flex-fill d-flex justify-content-start">
      <div class="col-md-12 mt-2">
        <div class="row mt-4">
          <div class="col-md-12">
            <strong class="mr-3">
              {{infoTotalModels}}
            </strong>

            <button
              class="btn btn-outline ml-3 mb-1"
              v-if="companies.length > 0"
              @click="changeCheckAll"
              :class="{ 'btn-outline-active': checkAll}"
            >
              {{ !checkAll ? 'Selecionar todas' : 'Desmarcar todas' }} ({{ meta.totalCount || 'Calculando' }})
            </button>
          </div>
        </div>

        <table class="table table-borderless table-dfe">
          <thead>
          <tr>
            <th>
              <BkpsCheckBox :value="checkAllRow" @input="changeCheckAllRow"/>
            </th>
            <th>ID</th>
            <th>Nome</th>
            <th>CNPJ</th>
            <th>Tipo</th>
            <th>UF</th>
            <th>IE</th>
            <th>IM</th>
            <th>E-mail</th>
            <th>Telefone</th>
            <th>Certificado Digital</th>
            <th>Criado em</th>
            <th>Atualizado em</th>
            <th>Status</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="company in companies" :key="company.id">
            <td>
              <BkpsCheckBox v-model="company.checked" @input="changeCheckRow"/>
            </td>
            <td>{{ company.id }}</td>
            <td>{{ company.name }}</td>
            <td>{{ company.cnpj | cnpj }}</td>
            <td>{{ company.company_type === 'parent' ? 'Matriz' : 'Filial' }}</td>
            <td>{{ company.uf }}</td>
            <td>{{ company.ie }}</td>
            <td>{{ company.im }}</td>
            <td>{{ company.email }}</td>
            <td>{{ company.phone }}</td>
            <td>
              <span v-if="company.certificate_expiring_at">
                {{ company.certificate_expiring_at | certificate }}
              </span>
              <span v-else>-</span>
            </td>
            <td>{{ company.created_at | datetime }}</td>
            <td>{{ company.updated_at | datetime }}</td>
            <td>{{ statusLabel(company) }}</td>
            <td>
              <button @click="showCompanyDetails(company)" class="btn btn-primary">
                Detalhes
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="col-md-12 my-1">
        <Pagination
          v-model="meta.currentPage"
          :total-count="meta.totalCount"
          :total-pages="meta.totalPages"
          @pagination="pagination"
        />
      </div>
    </div>

    <!-- Company Details Modal -->
    <Modal v-if="currentCompany" v-model="showModal">
      <div slot="header">
        <h3>{{ currentCompany.name }}</h3>
      </div>
      <div class="company-detail">
        <div class="row">
          <div class="col-md-6">
            <p><strong>ID:</strong> {{ currentCompany.id }}</p>
            <p><strong>Nome:</strong> {{ currentCompany.name }}</p>
            <p><strong>CNPJ:</strong> {{ currentCompany.cnpj | cnpj }}</p>
            <p><strong>Tipo:</strong> {{ currentCompany.company_type === 'parent' ? 'Matriz' : 'Filial' }}</p>
            <p><strong>UF:</strong> {{ currentCompany.uf }}</p>
            <p><strong>Código da Cidade:</strong> {{ currentCompany.city_code || '-' }}</p>
            <p><strong>IE:</strong> {{ currentCompany.ie || '-' }}</p>
            <p><strong>IM:</strong> {{ currentCompany.im || '-' }}</p>
            <p><strong>E-mail:</strong> {{ currentCompany.email || '-' }}</p>
            <p><strong>Telefone:</strong> {{ currentCompany.phone || '-' }}</p>
          </div>
          <div class="col-md-6">
            <p><strong>Certificado Expira em:</strong> {{ currentCompany.certificate_expiring_at ? (currentCompany.certificate_expiring_at | datetime) : '-' }}</p>
            <p><strong>Senha do Certificado:</strong> {{ currentCompany.certificate_password ? '******' : '-' }}</p>
            <p><strong>Contrato:</strong> {{ currentCompany.contract || '-' }}</p>
            <p><strong>Criado por:</strong> {{ currentCompany.created_by || '-' }}</p>
            <p><strong>Criado em:</strong> {{ currentCompany.created_at | datetime }}</p>
            <p><strong>Atualizado em:</strong> {{ currentCompany.updated_at | datetime }}</p>
            <p><strong>ID da Conta:</strong> {{ currentCompany.account_id || '-' }}</p>
            <p><strong>ID da Empresa Matriz:</strong> {{ currentCompany.company_id || '-' }}</p>
            <p v-if="currentCompany.metadata"><strong>Metadados:</strong></p>
            <pre v-if="currentCompany.metadata" class="metadata-json">{{ JSON.stringify(currentCompany.metadata, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
  import BadgeFilters from '@/components/shares/BadgeFilters'
  import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
  import CheckedModels from '@/mixins/CheckedModels'
  import ModelMapper from '@/mixins/ModelMapper'
  import handleSearchable from '@/models/handleSearchable'
  import GetUser from '@/mixins/GetUser'
  import GlobalLoading from '@/mixins/GlobalLoading'

  import Pagination from '@/components/shares/specialTag/Pagination'
  import Select2 from '@/components/shares/Select2'

  import { Filter } from '@/models/filter'
  import { ChevronDownIcon, SearchIcon } from 'vue-feather-icons'
  import Modal from '@/components/shares/specialTag/Modal'
  import { COMPANY_STATUS, COMPANY_STATUS_OPTIONS } from '@/utils/companyStatus'

  export default {
    components: {
      BadgeFilters,
      BkpsCheckBox,
      ChevronDownIcon,
      Modal,
      Pagination,
      SearchIcon,
      Select2
    },
    superMethods: {
      buildParams: ModelMapper.methods.buildParams
    },
    computed: {
      companies() {
        return this.getModels || []
      },
      disabledReport() {
        return this.meta.totalCount === 0 || (!this.checkAll && !this.companies.some(c => c.checked))
      },
      statusOptions() {
        return [
          { value: '', label: 'Todos' },
          ...COMPANY_STATUS_OPTIONS.map(opt => ({ value: opt.id, label: opt.friendlyName }))
        ]
      }
    },
    created() {
      this.defaultModelSetting.updateQueryURL = true
      this.order.push('name', 'asc')
      this.query.periodType = 'created_at'
      this.filterAccount = this.buildFilterAccount(this.allAccounts.data)
      this.filterEmail = this.buildFilterEmail(this.allUsers.data)
      this.filterUpdatedBy = this.buildFilterEmail(this.allUsers.data)
      this.asyncTemplateSelect({
        byURL: true,
        pagination: true
      })
    },
    data: () => ({
      filterAccount: null,
      filterEmail: null,
      filterUpdatedBy: null,
      filterId: new Filter({
        key: 'id',
        label: 'ID',
        type: 'text'
      }),

      filterCityCode: new Filter({
        key: 'cityCode',
        label: 'Código da Cidade',
        type: 'text'
      }),
      filterIe: new Filter({
        key: 'ie',
        label: 'Inscrição Estadual',
        type: 'text'
      }),
      filterIm: new Filter({
        key: 'im',
        label: 'Inscrição Municipal',
        type: 'text'
      }),
      filterCompanyId: new Filter({
        key: 'companyId',
        label: 'ID da Empresa Matriz',
        type: 'text'
      }),
      filterCertificate: new Filter({
        key: 'certificate',
        label: 'Certificado',
        type: 'select',
        table: [
          { label: 'Com certificado', value: 'true' },
          { label: 'Sem certificado', value: 'false' }
        ]
      }),

      filterPeriodType: new Filter({
        key: 'periodType',
        label: 'Tipo de período',
        type: 'select',
        table: [
          { label: 'Data de criação', value: 'created_at' },
          { label: 'Data de atualização', value: 'updated_at' },
          { label: 'Expiração do certificado', value: 'certificate_expiring_at' }
        ]
      }),
      filterEndDate: new Filter({
        key: 'endDate',
        label: 'Data final',
        type: 'date'
      }),
      filterStartDate: new Filter({
        key: 'startDate',
        label: 'Data inicial',
        type: 'date'
      }),
      filterType: new Filter({
        key: 'type',
        label: 'Tipo',
        type: 'select',
        table: [
          { label: 'Matriz', value: 'parent' },
          { label: 'Filial', value: 'subsidiary' }
        ]
      }),
      filterEntityStatus: new Filter({
        key: 'entityStatusId',
        label: 'Status',
        type: 'select',
        table: [
          { label: 'Ativo', value: COMPANY_STATUS.ENABLED },
          { label: 'Inativo', value: COMPANY_STATUS.DISABLED }
        ]
      }),
      filterUf: new Filter({
        key: 'uf',
        label: 'UF',
        type: 'select',
        table: [
          { label: 'AC', value: 'AC' },
          { label: 'AL', value: 'AL' },
          { label: 'AP', value: 'AP' },
          { label: 'AM', value: 'AM' },
          { label: 'BA', value: 'BA' },
          { label: 'CE', value: 'CE' },
          { label: 'DF', value: 'DF' },
          { label: 'ES', value: 'ES' },
          { label: 'GO', value: 'GO' },
          { label: 'MA', value: 'MA' },
          { label: 'MT', value: 'MT' },
          { label: 'MS', value: 'MS' },
          { label: 'MG', value: 'MG' },
          { label: 'PA', value: 'PA' },
          { label: 'PB', value: 'PB' },
          { label: 'PR', value: 'PR' },
          { label: 'PE', value: 'PE' },
          { label: 'PI', value: 'PI' },
          { label: 'RJ', value: 'RJ' },
          { label: 'RN', value: 'RN' },
          { label: 'RS', value: 'RS' },
          { label: 'RO', value: 'RO' },
          { label: 'RR', value: 'RR' },
          { label: 'SC', value: 'SC' },
          { label: 'SP', value: 'SP' },
          { label: 'SE', value: 'SE' },
          { label: 'TO', value: 'TO' }
        ]
      }),
      currentCompany: null,
      modelPathPrefix: 'admin',
      nameModel: 'Company',
      showModal: false
    }),
    methods: {

      find() {
        if (this.query.generic && this.isCnpjFormatted(this.query.generic)) {
          this.query.generic = this.removeCnpjFormatting(this.query.generic)
        }
        this.asyncTemplateSelect()
      },

      statusLabel(company) {
        return Number(company.entityStatusId) === COMPANY_STATUS.DISABLED ? 'Inativo' : 'Ativo'
      },

      isCnpjFormatted(value) {
        return /[0-9]{2}\.[0-9]{3}\.[0-9]{3}\/[0-9]{4}-[0-9]{2}/.test(value)
      },

      removeCnpjFormatting(value) {
        return value.replace(/[^0-9]/g, '')
      },
      buildParams(pagination) {
        if (this.query.generic)
          this.query.generic = handleSearchable(this.query.generic)

        const params = { ...this.$options.superMethods.buildParams.call(this, pagination) }

        if (params.q && (params.q.startDate || params.q.endDate)) {
          const periodType = params.q.periodType || 'created_at'

          if (params.q.startDate) {
            params.q[`${periodType}_start`] = params.q.startDate
            delete params.q.startDate
          }

          if (params.q.endDate) {
            params.q[`${periodType}_end`] = params.q.endDate
            delete params.q.endDate
          }

          delete params.q.periodType
        }

        if (params.q && params.q.cityCode) {
          params.q.city_code = params.q.cityCode
          delete params.q.cityCode
        }

        if (params.q && params.q.companyId) {
          params.q.company_id = params.q.companyId
          delete params.q.companyId
        }

        if (params.q && params.q.user_email) {
          params.q.user = params.q.user_email
          delete params.q.user_email
        }

        // Mantém updated_by como está
        if (params.q && params.q.updated_by) {
          // Não precisa converter, já está no formato correto
        }

        return params
      },
      changeCheckAll() {
        this.changeCheck({ checkAll: !this.checkAll, sources: this.companies })
      },
      changeCheckAllRow(value) {
        this.changeCheck({ checkAllRow: value, sources: this.companies })
      },
      changeCheckRow() {
        this.changeCheck({ sources: this.companies })
      },
      createReport() {
        let params
        let amount

        if (this.checkAll) {
          params = this.buildParams()
          amount = this.meta.totalCount
        } else {
          const ids = this.companies.filter(c => c.checked).map(c => c.id)
          params = { q: { id: ids } }
          amount = ids.length
        }

        if (!confirm(`Exportar ${amount} Empresa(s).\nTem certeza?`))
          return

        this.openGlobalLoad()
        this.axios.get('admin/companies/report', {params})
          .then(response => {
            if (response.data)
              this.$parse.downloadByLink(response.data)
            else
              this.$notify.add(response.meta.notice)
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
      showCompanyDetails(company) {
        this.currentCompany = company
        this.showModal = true
      },
      updateOrder(event) {
        const option = event.target.value

        const [name, order] = {
          '1': ['name', 'asc'],
          '2': ['name', 'desc'],
          '3': ['cnpj', 'asc'],
          '4': ['cnpj', 'desc'],
          '5': ['created_at', 'asc'],
          '6': ['created_at', 'desc'],
          '7': ['updated_at', 'asc'],
          '8': ['updated_at', 'desc'],
        }[option]

        this.order = [name, order]
        this.find()
      }
    },
    inject: ['allAccounts', 'allUsers', 'buildFilterAccount', 'buildFilterEmail', 'getAccountName'],
    mixins: [CheckedModels, ModelMapper, GetUser, GlobalLoading],
    name: 'AdminCompaniesIndex',
    watch: {
      allAccounts: {
        deep: true,
        handler(value) {
          this.filterAccount = this.buildFilterAccount(value.data)
        },
      },
      allUsers: {
        deep: true,
        handler(value) {
          this.filterEmail = this.buildFilterEmail(value.data)
          this.filterUpdatedBy = this.buildFilterEmail(value.data)
        },
      }
    }
  }
</script>

<style scoped>
  .company-detail {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 5px;
    color: var(--primary-text-color);
  }

  [data-theme="dark"] .company-detail {
    background-color: #2a3847;
  }

  .company-detail p,
  .company-detail strong {
    color: var(--primary-text-color);
  }

  .company-detail p {
    margin-bottom: 8px;
  }

  .metadata-json {
    background-color: #051724;
    color: #fff;
    padding: 10px;
    border-radius: 5px;
    font-size: 12px;
    max-height: 200px;
    overflow-y: auto;
    
  }
</style>
